import { Component, inject, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { TypeOptions } from '@app/interface/type-options';
import { MatCardModule } from '@angular/material/card';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DialogData } from '@app/new-company/new-company.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { ReferralRequestInterface } from '@app/interface/referral-request';
import { ReferralRequestApi } from '@app/services/referral-request-api.service';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastService } from '@app/services/toast.service';

@Component({
  selector: 'izy-new-referral-request-dialog',
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    // MatDialogClose,
    MatSelectModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatIconModule,
    MatDividerModule,
    ReactiveFormsModule,
  ],
  standalone: true,
  templateUrl: './new-referral-request-dialog.component.html',
  styleUrl: './new-referral-request-dialog.component.scss',
})
export class NewReferralRequestDialogComponent {
  private toast = inject(ToastService);
  showDelay = new FormControl(700);
  hideDelay = new FormControl(1000);
  isLoading = false;

  // Form group for better validation
  referralForm: FormGroup;

  // Organization ID from route
  organizationId: number | null = null;

  // Status options for select dropdown
  statusOptions = [
    { value: 'PENDING', label: 'In attesa' },
    { value: 'ACCEPTED', label: 'Accettato' },
    { value: 'EXPIRED', label: 'Scaduto' },
    { value: 'FAILED', label: 'Rifiutato' },
  ];

  // Privacy consent options
  privacyOptions = [
    {
      value: 'privacy_1',
      label:
        'Acconsento al trattamento dei dati personali per la gestione della richiesta referral *',
      required: true,
    },
  ];

  newRequest: Partial<ReferralRequestInterface> = {
    name: '',
    mail: '',
    date: '',
    telephone: '',
    hour: '',
    status: 'PENDING',
    reward_issued_to_old_organization: false,
    reward_issued_to_new_organization: false,
    is_already_partita_iva: false,
    discount_code: '',
    is_deleted: false,
  };

  constructor(
    private referralRequestApi: ReferralRequestApi,
    private route: ActivatedRoute,
    private router: Router,
    public dialogRef: MatDialogRef<NewReferralRequestDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { organizationId: number },
  ) {
    this.organizationId = data.organizationId;

    // Initialize form with validators - ADDED telephone field
    this.referralForm = new FormGroup({
      name: new FormControl('', [Validators.required, Validators.minLength(2)]),
      mail: new FormControl('', [Validators.required, Validators.email]),
      telephone: new FormControl('', [Validators.required]),
      is_already_partita_iva: new FormControl(false),
      status: new FormControl('PENDING', Validators.required),
      discount_code: new FormControl(''),
      privacy_1: new FormControl(false, Validators.requiredTrue),
    });
  }

  ngOnInit(): void {
    // Set current date and time
    const now = new Date();
    this.newRequest.date = now.toISOString().split('T')[0]; // YYYY-MM-DD
    this.newRequest.hour = now.toTimeString().split(' ')[0]; // HH:MM:SS
  }

  /**
   * Create a new referral request
   */
  createReferralRequest(): void {
    // Mark all form fields as touched to trigger validation messages
    this.referralForm.markAllAsTouched();

    if (this.referralForm.invalid) {
      this.showErrorMessage(
        'Per favore, compila tutti i campi obbligatori correttamente.',
      );
      return;
    }

    if (!this.organizationId) {
      this.showErrorMessage('ID organizzazione non valido.');
      return;
    }

    this.isLoading = true;

    // Prepare the request data from form values
    const requestData: Partial<ReferralRequestInterface> = {
      ...this.newRequest,
      name: this.referralForm.get('name')?.value,
      mail: this.referralForm.get('mail')?.value,
      telephone: this.referralForm.get('telephone')?.value,
      is_already_partita_iva: this.referralForm.get('is_already_partita_iva')
        ?.value,
      date: this.newRequest.date,
      hour: this.newRequest.hour,
      organization_id: this.organizationId,
    };

    console.log('requestData', requestData);

    this.referralRequestApi
      .createReferralRequest(this.organizationId, requestData)
      .subscribe({
        next: (response: ReferralRequestInterface) => {
          this.isLoading = false;
          this.showSuccessMessage('Richiesta referral creata con successo!');
          this.dialogRef.close(response); // This passes the created request back
        },
        error: (err: any) => {
          this.isLoading = false;
          console.error('Error creating referral request:', err);
          this.showErrorMessage(
            'Errore durante la creazione della richiesta. Riprova più tardi.',
          );
          // You might want to close with false or nothing on error
          // this.dialogRef.close(false);
        },
      });
  }

  /**
   * Check if all required privacy consents are accepted
   */
  areAllRequiredConsentsAccepted(): boolean {
    return this.referralForm.get('privacy_1')?.value === true;
  }

  /**
   * Generate a random discount code
   */
  private generateDiscountCode(): string {
    const prefix = 'REF';
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    return `${prefix}${randomNum}`;
  }

  /**
   * Close the dialog without creating
   */
  onCancel(): void {
    this.dialogRef.close(false); // Pass false to indicate cancellation/failure
  }
  /**
   * Reset the form
   */
  resetForm(): void {
    this.referralForm.reset({
      name: '',
      mail: '',
      is_already_partita_iva: false,
      status: 'PENDING',
      discount_code: '',
      notes: '',
    });
  }

  /**
   * Check if a form field is invalid
   */
  isFieldInvalid(fieldName: string): boolean {
    const field = this.referralForm.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  /**
   * Get error message for a field
   */
  getErrorMessage(fieldName: string): string {
    const field = this.referralForm.get(fieldName);

    if (field?.hasError('required')) {
      return 'Questo campo è obbligatorio';
    }

    if (field?.hasError('email')) {
      return 'Inserisci un indirizzo email valido';
    }

    if (field?.hasError('minlength')) {
      return 'Il nome deve contenere almeno 2 caratteri';
    }

    // Add telephone validation if needed
    if (fieldName === 'telephone' && field?.hasError('pattern')) {
      return 'Inserisci un numero di telefono valido';
    }

    return '';
  }

  private showSuccessMessage(message: string): void {
    this.toast.success(message);
  }

  private showErrorMessage(message: string): void {
    this.toast.error(message);
  }
}

@Component({
  selector: 'izy-new-referral-request-page-1-dialog',
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    // MatDialogClose,
    MatSelectModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatIconModule,
    MatDividerModule,
    ReactiveFormsModule,
  ],
  standalone: true,
  templateUrl: './new-referral-request-page-1-dialog.component.html',
  styleUrl: './new-referral-request-dialog.component.scss',
})
export class NewReferralRequestPage1DialogComponent {
  showDelay = new FormControl(700);
  hideDelay = new FormControl(1000);
  isLoading = false;

  constructor(
    public dialogRef: MatDialogRef<NewReferralRequestPage1DialogComponent>,
  ) {}

  closeDialog(): void {
    this.dialogRef.close(false);
  }

  confirm(): void {
    this.dialogRef.close(true);
  }
}

@Component({
  selector: 'izy-new-referral-request-page-3-dialog',
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    // MatDialogClose,
    MatSelectModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatIconModule,
    MatDividerModule,
    ReactiveFormsModule,
  ],
  standalone: true,
  templateUrl: './new-referral-request-page-3-dialog.component.html',
  styleUrl: './new-referral-request-dialog.component.scss',
})
export class NewReferralRequestPage3DialogComponent {
  showDelay = new FormControl(700);
  hideDelay = new FormControl(1000);
  isLoading = false;

  constructor(
    public dialogRef: MatDialogRef<NewReferralRequestPage3DialogComponent>,
  ) {}

  closeDialog(): void {
    this.dialogRef.close(false);
  }

  confirm(): void {
    this.dialogRef.close(true);
  }
}
